import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Link,
  useTheme,
  alpha,
  Divider,
  Chip,
} from "@mui/material";
import {
  LockOutlined,
  SecurityOutlined,
  VpnKeyOutlined,
  ShieldOutlined,
  CloudOutlined,
  VerifiedUserOutlined,
  EmailOutlined,
  BugReportOutlined,
  CheckCircleOutline,
  ScheduleOutlined,
} from "@mui/icons-material";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode } from "../utils";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface SecurityProps {
  mode: string;
}

const Security: React.FC<SecurityProps> = ({ mode }) => {
  const theme = useTheme();
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Get logo URL with dark mode support
  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";
  // Use whitelabel security email, owner email, sales email, or construct from domain
  const securityEmail = whitelabelSettings?.securityEmail || whitelabelSettings?.ownerEmail || whitelabelSettings?.salesEmail || "security@" + window.location.hostname;
  // Check if this is the main platform (has specific security resources like PGP key)
  const isMainPlatform = whitelabelSettings?.isMainSite === true;

  const dataProtections = [
    "Data encryption at rest (AES-256)",
    "Encryption in transit (TLS 1.2+)",
    "LLM no training policy by default",
    "Zero Data Retention upon request",
    "Audit logging",
    "Automatic PII redaction in logs",
  ];

  const authenticationAccess = [
    "Single Sign-On (SSO)",
    "Multi-Factor Authentication (MFA)",
    "Role-Based Access Control (RBAC)",
    "API key management",
    "JWT-based authentication",
  ];

  const infrastructureSecurity = [
    "99.9% uptime SLA",
    "Automatic backups",
    "DDoS protection",
    "Web Application Firewall (WAF)",
    "Auto-scaling infrastructure",
  ];

  const complianceGovernance = [
    "CSA STAR Level 1",
    "Trusted Site",
    "GDPR/CCPA Compliant",
    "SOC 2 Type II partner infrastructure",
  ];

  const workingTowards = [
    "SOC 2 Type I (Planned 2026)",
    "ISO 27001 (Planned 2026)",
    "HIPAA (Planned 2026)",
  ];

  const vendorSecurityPages = [
    {
      name: "Open Router (LLM Provider)",
      url: "https://trust.openrouter.ai",
    },
    {
      name: "Digital Ocean",
      url: "https://www.digitalocean.com/trust/resources",
    },
  ];

  const whatToInclude = [
    "Description of the vulnerability",
    "Steps to reproduce the issue",
    "Potential impact of the vulnerability",
    "Any proof-of-concept code (if applicable)",
    "Your contact information for follow-up",
  ];

  const ourCommitment = [
    "We will acknowledge receipt of your report within 48 hours",
    "We will provide regular updates on our progress",
    "We will notify you when the vulnerability is fixed",
    "We will credit you in our Hall of Fame (if desired)",
  ];

  const SectionCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    items: string[];
    chipLabel?: string;
  }> = ({ icon, title, items, chipLabel }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: alpha(instanceConfig.primaryColor, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: instanceConfig.primaryColor,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6">{title}</Typography>
          {chipLabel && (
            <Chip
              label={chipLabel}
              size="small"
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          {items.map((item, idx) => (
            <Typography
              key={idx}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {item}
            </Typography>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Security
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto" }}>
            At {appName}, we take the security of our systems and customer data
            seriously. We appreciate the security research community's efforts
            in helping us maintain a safe environment.
          </Typography>
        </Container>
      </Box>

      {/* Main Security Features */}
      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        <Grid container spacing={4}>
          {/* Data Protections */}
          <Grid item xs={12} md={6}>
            <SectionCard
              icon={<LockOutlined />}
              title="Data Protections"
              items={dataProtections}
            />
          </Grid>

          {/* Authentication & Access */}
          <Grid item xs={12} md={6}>
            <SectionCard
              icon={<VpnKeyOutlined />}
              title="Authentication & Access"
              items={authenticationAccess}
            />
          </Grid>

          {/* Infrastructure Security */}
          <Grid item xs={12} md={6}>
            <SectionCard
              icon={<CloudOutlined />}
              title="Infrastructure Security"
              items={infrastructureSecurity}
            />
          </Grid>

          {/* Compliance & Governance */}
          <Grid item xs={12} md={6}>
            <SectionCard
              icon={<VerifiedUserOutlined />}
              title="Compliance & Governance"
              items={complianceGovernance}
            />
          </Grid>
        </Grid>

        {/* Working Towards */}
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: alpha(instanceConfig.primaryColor, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: instanceConfig.primaryColor,
                  mr: 2,
                }}
              >
                <ScheduleOutlined />
              </Box>
              <Typography variant="h6">Working Towards</Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {workingTowards.map((item, idx) => (
                <Chip
                  key={idx}
                  label={item}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Vendor Security Pages */}
        <Typography variant="h5" sx={{ mt: 6, mb: 3 }}>
          Vendor Security Pages
        </Typography>
        <Grid container spacing={2}>
          {vendorSecurityPages.map((vendor, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: theme.shadows[4] },
                }}
                onClick={() => window.open(vendor.url, "_blank")}
              >
                <CardContent sx={{ textAlign: "center" }}>
                  <ShieldOutlined
                    sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="subtitle2">{vendor.name}</Typography>
                  <Link
                    href={vendor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: "0.75rem" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    View Security Page
                  </Link>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 6 }} />

        {/* Reporting a Vulnerability */}
        <Typography variant="h4" gutterBottom>
          Reporting a Vulnerability
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          If you believe you've discovered a security vulnerability in our
          products or services, we encourage you to report it to us responsibly.
        </Typography>

        <Grid container spacing={4} sx={{ mt: 2 }}>
          {/* How to Report */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <EmailOutlined
                    sx={{ color: instanceConfig.primaryColor, mr: 1 }}
                  />
                  <Typography variant="h6">How to Report</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong>{" "}
                    <Link href={`mailto:${securityEmail}`}>{securityEmail}</Link>
                  </Typography>
                </Box>
                {isMainPlatform && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>PGP Key:</strong>{" "}
                    <Link href="/.well-known/pgp-key.txt">
                      Download our PGP key
                    </Link>{" "}
                    for encrypted communications
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* What to Include */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <BugReportOutlined
                    sx={{ color: instanceConfig.primaryColor, mr: 1 }}
                  />
                  <Typography variant="h6">What to Include</Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {whatToInclude.map((item, idx) => (
                    <Typography
                      key={idx}
                      component="li"
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Our Commitment */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CheckCircleOutline
                    sx={{ color: instanceConfig.primaryColor, mr: 1 }}
                  />
                  <Typography variant="h6">Our Commitment</Typography>
                </Box>
                <Grid container spacing={2}>
                  {ourCommitment.map((item, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <CheckCircleOutline
                          sx={{
                            color: instanceConfig.primaryColor,
                            fontSize: 20,
                            mr: 1,
                            mt: 0.25,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Resources */}
        <Typography variant="h5" sx={{ mt: 6, mb: 3 }}>
          Resources
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Link
            href="/vulnerability-disclosure-policy"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 3,
              py: 1.5,
              borderRadius: 1,
              bgcolor: alpha(instanceConfig.primaryColor, 0.1),
              color: instanceConfig.primaryColor,
              textDecoration: "none",
              fontWeight: "medium",
              "&:hover": {
                bgcolor: alpha(instanceConfig.primaryColor, 0.2),
              },
            }}
          >
            Vulnerability Disclosure Policy
          </Link>
          <Link
            href="/security/hall-of-fame"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 3,
              py: 1.5,
              borderRadius: 1,
              bgcolor: alpha(instanceConfig.primaryColor, 0.1),
              color: instanceConfig.primaryColor,
              textDecoration: "none",
              fontWeight: "medium",
              "&:hover": {
                bgcolor: alpha(instanceConfig.primaryColor, 0.2),
              },
            }}
          >
            Security Hall of Fame
          </Link>
          {isMainPlatform && (
            <Link
              href="/.well-known/security.txt"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 3,
                py: 1.5,
                borderRadius: 1,
                bgcolor: alpha(instanceConfig.primaryColor, 0.1),
                color: instanceConfig.primaryColor,
                textDecoration: "none",
                fontWeight: "medium",
                "&:hover": {
                  bgcolor: alpha(instanceConfig.primaryColor, 0.2),
                },
              }}
            >
              security.txt
            </Link>
          )}
        </Box>
      </Container>

      <BrandedPageFooter currentPage="security" />
    </Box>
  );
};

export default Security;
